import type { StyleId } from '@alltrails/maps/types/Styles';

/** Persisted to localStorage, so renaming an id drops that setting for every user. */
export type MapContentId = LocalMapContentId | ToggledMapContentId;

/** Content that is not a map style. */
export type LocalMapContentId = 'cursorHints' | 'mapCoordinates' | 'routePhotos' | 'routeDirectionArrows' | 'routeSnapPreview' | 'trails' | 'pois';

type ToggledMapContentId =
  | 'distanceMarkers'
  | 'userWaypoints'
  | 'communityWaypoints'
  | 'communityActivity'
  | 'savedRoutePolyline'
  | 'waymarkedHiking'
  | 'waymarkedCycling'
  | 'waymarkedMTB';

type MapContentEntryBase = {
  label: string;
};

export type LocalMapContentEntry = MapContentEntryBase & {
  defaultVisible: boolean;
  id: LocalMapContentId;
  styleId?: never;
};

type ToggledMapContentEntry = MapContentEntryBase & {
  defaultVisible?: never;
  id: ToggledMapContentId;
  styleId: StyleId;
};

export type MapContentEntry = LocalMapContentEntry | ToggledMapContentEntry;

export type MapContentGroup = {
  entries: readonly MapContentEntry[];
  id: string;
  title: string;
};

// Group titles and entry labels stay hardcoded English until ADV-335 extracts this surface's strings
export const mapContentGroups = [
  {
    id: 'general',
    title: 'General',
    entries: [
      { id: 'mapCoordinates', label: 'Map coordinates', defaultVisible: true },
      // What a click does where the cursor is, such as "Connect to next segment". Not the path preview's stats, which are data.
      { id: 'cursorHints', label: 'Cursor hints', defaultVisible: true }
    ]
  },
  {
    id: 'route',
    title: 'Route',
    entries: [
      // Off by default so a freshly opened map makes no photo tour request
      { id: 'routePhotos', label: 'Photos', defaultVisible: false },
      { id: 'routeDirectionArrows', label: 'Direction arrows', defaultVisible: true },
      { id: 'routeSnapPreview', label: 'Path preview', defaultVisible: true },
      { id: 'distanceMarkers', label: 'Distance markers', styleId: 'distanceMarkers' },
      { id: 'savedRoutePolyline', label: 'Last saved route', styleId: 'savedRoutePolyline' }
    ]
  },
  {
    id: 'nearby',
    title: 'Nearby',
    // Off by default so a freshly opened map makes no explore-service request
    entries: [
      { id: 'trails', label: 'Trails', defaultVisible: false },
      { id: 'pois', label: 'Points of interest', defaultVisible: false }
    ]
  },
  {
    id: 'community',
    title: 'Community content',
    entries: [
      { id: 'userWaypoints', label: 'Your waypoints', styleId: 'userWaypoints' },
      { id: 'communityWaypoints', label: 'Community waypoints', styleId: 'communityWaypoints' },
      { id: 'communityActivity', label: 'Community activity', styleId: 'communityActivity' }
    ]
  },
  {
    id: 'waymarkedTrails',
    title: 'Waymarked trails',
    entries: [
      { id: 'waymarkedHiking', label: 'Waymarked hiking', styleId: 'waymarkedHiking' },
      { id: 'waymarkedCycling', label: 'Waymarked cycling', styleId: 'waymarkedCycling' },
      { id: 'waymarkedMTB', label: 'Waymarked MTB', styleId: 'waymarkedMTB' }
    ]
  }
] as const satisfies readonly MapContentGroup[];

export type MapContentGroupId = (typeof mapContentGroups)[number]['id'];

export const mapContentEntries = mapContentGroups.flatMap<MapContentEntry>(group => group.entries);

export const mapContentEntriesById = Object.fromEntries(mapContentEntries.map(entry => [entry.id, entry])) as Record<MapContentId, MapContentEntry>;

export const isToggledMapContentEntry = (entry: MapContentEntry): entry is ToggledMapContentEntry => entry.styleId !== undefined;

/** Subtracted from the Layers panel, so every style has exactly one panel that applies it. */
export const mapDetailsOwnedStyleIds: StyleId[] = mapContentEntries.filter(isToggledMapContentEntry).map(entry => entry.styleId);
